export interface User {
  id: string;
  email: string;
  name?: string;
  createdAt: string;
  subscription?: {
    status: 'active' | 'inactive' | 'trial';
    plan: 'basic' | 'premium';
    expiresAt?: string;
  };
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Local storage keys
const AUTH_TOKEN_KEY = 'orca_auth_token';
const USER_DATA_KEY = 'orca_user_data';

export class AuthService {
  private static instance: AuthService;
  private authState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: true
  };
  private listeners: ((state: AuthState) => void)[] = [];

  private constructor() {
    this.loadStoredAuth();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private loadStoredAuth() {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const userData = localStorage.getItem(USER_DATA_KEY);
      
      if (token && userData) {
        const user = JSON.parse(userData) as User;
        this.setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false
        });
      } else {
        this.setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Error loading stored auth:', error);
      this.setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false
      });
    }
  }

  private setAuthState(newState: AuthState) {
    this.authState = newState;
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.authState));
  }

  subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  getAuthState(): AuthState {
    return this.authState;
  }

  getAuthToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { getApiUrl, getAuthHeaders } = await import('./config');
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem(AUTH_TOKEN_KEY, data.token);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(data.user));
        
        this.setAuthState({
          user: data.user,
          isAuthenticated: true,
          isLoading: false
        });

        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async register(email: string, password: string, name?: string, accessCode?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name, accessCode }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem(AUTH_TOKEN_KEY, data.token);
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(data.user));
        
        this.setAuthState({
          user: data.user,
          isAuthenticated: true,
          isLoading: false
        });

        return { success: true };
      } else {
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  logout() {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    
    this.setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false
    });
  }

  async refreshAuth(): Promise<boolean> {
    const token = this.getAuthToken();
    if (!token) {
      this.logout();
      return false;
    }

    try {
      const { getApiUrl } = await import('./config');
      const response = await fetch(getApiUrl('/api/auth/verify'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(data.user));
        
        this.setAuthState({
          user: data.user,
          isAuthenticated: true,
          isLoading: false
        });
        
        return true;
      } else {
        if (response.status === 401 || response.status === 403) {
          console.log('Auth token invalid, logging out...');
          this.logout();
        } else {
          console.warn('Auth verification failed with status:', response.status, 'but not logging out');
          this.setAuthState({
            ...this.authState,
            isLoading: false
          });
        }
        return false;
      }
    } catch (error) {
      console.error('Auth refresh error:', error);
      this.setAuthState({
        ...this.authState,
        isLoading: false
      });
      return false;
    }
  }

  handleApiError(response: Response): boolean {
    if (response.status === 401 || response.status === 403) {
      console.log('API call returned auth error, refreshing token...');
      this.refreshAuth();
      return true;
    }
    return false;
  }
} 